import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { css, StyleSheet } from 'aphrodite'
import ReactFlow, {
  Background,
  ControlButton,
  Controls,
  MarkerType,
  applyNodeChanges,
} from 'reactflow'
import 'reactflow/dist/style.css'

import { ChatbotMessageNode } from './ChatbotMessageNode'
import { ChatbotContactNode } from './ChatbotContactNode'
import { ChatbotTemplateNode } from './ChatbotTemplateNode'
import { ChatbotStartChatbotNode } from './ChatbotStartChatbotNode'
import { ChatbotAgentNode } from './ChatbotAgentNode'
import { ChatbotDeletableEdge } from './ChatbotDeletableEdge'
import { ChatbotAddNodeMenu } from './ChatbotAddNodeMenu'
import { HANDLE_OUT, NODE_PALETTE, NODE_TYPE_AGENT, NODE_TYPE_CONTACT, NODE_TYPE_MESSAGE, NODE_TYPE_START_CHATBOT, NODE_TYPE_TEMPLATE, autoLayoutChatbot, edgeSourceHandle, getNodeById, getOutgoingHandleIds, toFlowEdges, toFlowNodes } from './chatbotGraph'
import { overlayTemplateNodeDataFromMap } from './chatbotTemplatePreview'
import { UiHelper } from '../../../../common/helpers/UiHelper'
import { AppTheme } from '../../../../styles/AppTheme'
import { labelForChatbotId } from './chatbotStartChatbots'
import { labelForAgentProviderId } from './chatbotAgentForm'

const nodeTypes = {
  [NODE_TYPE_MESSAGE]: ChatbotMessageNode,
  [NODE_TYPE_CONTACT]: ChatbotContactNode,
  [NODE_TYPE_TEMPLATE]: ChatbotTemplateNode,
  [NODE_TYPE_START_CHATBOT]: ChatbotStartChatbotNode,
  [NODE_TYPE_AGENT]: ChatbotAgentNode,
}

const edgeTypes = {
  deletable: ChatbotDeletableEdge,
}

const edgeMarker = { type: MarkerType.ArrowClosed, color: AppTheme.primaryColor }
const edgeStyle = { stroke: AppTheme.primaryColor, strokeDasharray: '6 4' }

const defaultEdgeOptions = {
  type: 'deletable',
  markerEnd: edgeMarker,
  style: edgeStyle,
}

const fitViewOptions = {
  padding: 0.35,
  maxZoom: 0.85,
}

const NODE_CARD_WIDTH = 240
const ADD_OFFSET_X = NODE_CARD_WIDTH / 2
const ADD_OFFSET_Y = 40

function incomingTargetIds(edges) {
  const ids = {}
  const list = edges || []
  for (let i = 0; i < list.length; i++) {
    const id = list[i] && list[i].targetNodeId
    if (id) ids[id] = true
  }
  return ids
}

function nodeDataSig(data) {
  const d = data || {}
  const copy = Object.assign({}, d)
  delete copy.onDelete
  delete copy.onDuplicate
  copy.onSetStartNode = !!d.onSetStartNode
  return JSON.stringify(copy)
}

function mergeFlowNodes(prev, next) {
  const prevList = prev || []
  const nextList = next || []
  const prevById = {}
  for (let i = 0; i < prevList.length; i++) prevById[prevList[i].id] = prevList[i]
  let changed = prevList.length !== nextList.length
  const merged = nextList.map(n => {
    const p = prevById[n.id]
    if (!p) {
      changed = true
      return n
    }
    const samePos = p.position && n.position && p.position.x === n.position.x && p.position.y === n.position.y
    const sameType = p.type === n.type
    const sameData = nodeDataSig(p.data) === nodeDataSig(n.data)
    if (samePos && sameType && sameData) return p
    changed = true
    return Object.assign({}, p, {
      type: n.type,
      position: n.position,
      data: n.data,
      style: n.style,
    })
  })
  return changed ? merged : prevList
}

function nodeIdFromElement(el) {
  if (el && el.closest) {
    const nodeEl = el.closest('.react-flow__node')
    return nodeEl ? nodeEl.getAttribute('data-id') : null
  }
  while (el && el !== document) {
    if (el.classList && el.classList.contains('react-flow__node')) {
      return el.getAttribute('data-id')
    }
    el = el.parentElement
  }
  return null
}

function nodeIdFromEvent(event) {
  let el = event && event.target
  if (event && event.changedTouches && event.changedTouches[0]) {
    const t = event.changedTouches[0]
    el = document.elementFromPoint(t.clientX, t.clientY)
  }
  return nodeIdFromElement(el)
}

function nodeIdFromPoint(x, y) {
  if (x == null || y == null || typeof document.elementFromPoint !== 'function') return null
  return nodeIdFromElement(document.elementFromPoint(x, y))
}

function isEditableTarget(target) {
  if (!target) return false
  const tag = target.tagName
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true
  if (target.isContentEditable) return true
  if (target.closest) return !!target.closest('input, textarea, select, [contenteditable="true"]')
  return false
}

function canvasZoomDirection(event) {
  if (!event || event.altKey) return null
  const key = event.key
  const code = event.code
  if (key === '+' || key === '=' || code === 'NumpadAdd') return 'in'
  if (key === '-' || code === 'NumpadSubtract') return 'out'
  return null
}

function eventClientPoint(event) {
  if (!event) return null
  if (event.changedTouches && event.changedTouches[0]) {
    const t = event.changedTouches[0]
    if (t.clientX == null || t.clientY == null) return null
    return { x: t.clientX, y: t.clientY }
  }
  if (event.clientX == null || event.clientY == null) return null
  return { x: event.clientX, y: event.clientY }
}

function isPointInsideEl(el, point) {
  if (!el || !point) return false
  const rect = el.getBoundingClientRect()
  return point.x >= rect.left && point.x <= rect.right && point.y >= rect.top && point.y <= rect.bottom
}

export const ChatbotFlowCanvas = React.forwardRef(function ChatbotFlowCanvas(props, ref) {
  const chatbot = props.chatbot || { nodes: [], edges: [] }
  const onNodeDragStop = props.onNodeDragStop
  const onConnect = props.onConnect
  const onAddConnectedNode = props.onAddConnectedNode
  const onEdgeDelete = props.onEdgeDelete
  const onNodeDelete = props.onNodeDelete
  const onNodeDuplicate = props.onNodeDuplicate
  const onSetStartNode = props.onSetStartNode
  const onNodeToggleDefault = props.onNodeToggleDefault
  const onNodeClick = props.onNodeClick
  const onAutoLayout = props.onAutoLayout
  const orgId = props.orgId
  const chatbotOptions = props.chatbotOptions || []
  const chatbotLabels = props.chatbotLabels || {}
  const agentProviderOptions = props.agentProviderOptions || []
  const palette = props.palette || NODE_PALETTE
  const templatePreviewByKey = props.templatePreviewByKey || {}
  const wrapperRef = useRef(null)
  const instanceRef = useRef(null)
  const pointerInsideRef = useRef(false)

  const chatbotNodes = chatbot.nodes
  const chatbotEdges = chatbot.edges
  const incomingIds = useMemo(() => incomingTargetIds(chatbotEdges), [chatbotEdges])
  const flowNodes = useMemo(() => {
    return toFlowNodes(chatbotNodes).map(n => {
      const canSetStart = !(n.data && n.data.isStartNode) && !incomingIds[n.id]
      const baseData = n.type === NODE_TYPE_TEMPLATE
        ? overlayTemplateNodeDataFromMap(n.data, templatePreviewByKey)
        : (n.data || {})
      const data = {
        ...baseData,
        onDelete: onNodeDelete ? () => onNodeDelete(n.id) : undefined,
        onDuplicate: onNodeDuplicate ? () => onNodeDuplicate(n.id) : undefined,
        onSetStartNode: canSetStart && onSetStartNode ? () => onSetStartNode(n.id) : undefined,
        onToggleDefault: onNodeToggleDefault ? () => onNodeToggleDefault(n.id) : undefined,
      }
      if (n.type === NODE_TYPE_CONTACT && n.data && n.data.state) {
        data.statusLabel = UiHelper.renameCRMContactLeadStatus(orgId, n.data.state)
      }
      if (n.type === NODE_TYPE_START_CHATBOT && n.data && n.data.chatbotId) {
        data.chatbotLabel = labelForChatbotId(chatbotOptions, n.data.chatbotId, chatbotLabels)
      }
      if (n.type === NODE_TYPE_AGENT && n.data && n.data.providerId) {
        data.providerLabel = labelForAgentProviderId(agentProviderOptions, n.data.providerId)
      }
      return { ...n, data }
    })
  }, [chatbotNodes, chatbotOptions, chatbotLabels, agentProviderOptions, incomingIds, onNodeDelete, onNodeDuplicate, onNodeToggleDefault, onSetStartNode, orgId, templatePreviewByKey])
  const flowEdges = useMemo(() => {
    return toFlowEdges(chatbotEdges).map(e => ({
      ...e,
      data: { onDelete: onEdgeDelete },
      markerEnd: edgeMarker,
      style: edgeStyle,
    }))
  }, [chatbotEdges, onEdgeDelete])

  const [nodes, setNodes] = useState(flowNodes)
  const [edges, setEdges] = useState(flowEdges)
  const didDragRef = useRef(false)
  const connectingFromRef = useRef(null)
  const didConnectRef = useRef(false)
  const fitPendingRef = useRef(false)
  const [addMenu, setAddMenu] = useState(null)

  useImperativeHandle(ref, () => ({
    getAddPosition() {
      const inst = instanceRef.current
      const el = wrapperRef.current
      if (!inst || !el || typeof inst.screenToFlowPosition !== 'function') return null
      const rect = el.getBoundingClientRect()
      const p = inst.screenToFlowPosition({
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
      })
      if (!p || p.x == null || p.y == null) return null
      return {
        posX: p.x - ADD_OFFSET_X,
        posY: p.y - ADD_OFFSET_Y,
      }
    },
  }), [])

  const handleInit = useCallback((inst) => {
    instanceRef.current = inst
    const runFit = () => {
      const list = inst.getNodes && inst.getNodes()
      if (list && list.length) inst.fitView(fitViewOptions)
    }
    requestAnimationFrame(runFit)
  }, [])

  useEffect(() => {
    setNodes(prev => mergeFlowNodes(prev, flowNodes))
  }, [flowNodes])

  useEffect(() => {
    setEdges(flowEdges)
  }, [flowEdges])

  useEffect(() => {
    if (!fitPendingRef.current) return
    fitPendingRef.current = false
    const inst = instanceRef.current
    if (inst) requestAnimationFrame(() => inst.fitView(fitViewOptions))
  }, [nodes])

  const handleAutoLayout = useCallback(() => {
    const inst = instanceRef.current
    if (!inst || !onAutoLayout) return
    const next = autoLayoutChatbot(chatbot, inst.getNodes())
    if (next === chatbot) {
      inst.fitView(fitViewOptions)
      return
    }
    fitPendingRef.current = true
    onAutoLayout(next)
  }, [chatbot, onAutoLayout])

  const onNodesChange = useCallback((changes) => {
    const next = []
    for (let i = 0; i < changes.length; i++) {
      if (changes[i].type !== 'remove') next.push(changes[i])
    }
    setNodes(nds => applyNodeChanges(next, nds))
  }, [])

  const isValidConnection = useCallback((connection) => {
    if (!connection || !connection.source || !connection.target) return false
    if (connection.source === connection.target) return false
    const targetNode = getNodeById(chatbot, connection.target)
    if (targetNode && targetNode.isStartNode) return false
    const sourceNode = getNodeById(chatbot, connection.source)
    const sourceData = sourceNode && sourceNode.type === NODE_TYPE_TEMPLATE
      ? overlayTemplateNodeDataFromMap(sourceNode.data, templatePreviewByKey)
      : (sourceNode && sourceNode.data)
    const validHandles = getOutgoingHandleIds(sourceData, sourceNode && sourceNode.type)
    if (!validHandles.length) return false
    const handle = connection.sourceHandle || HANDLE_OUT
    if (validHandles.indexOf(handle) === -1) return false
    const existing = (chatbot.edges || []).some(e => (
      e.sourceNodeId === connection.source && edgeSourceHandle(e) === handle
    ))
    return !existing
  }, [chatbot, templatePreviewByKey])

  const handleConnect = useCallback((params) => {
    didConnectRef.current = true
    if (!params || params.source === params.target) return
    if (onConnect) onConnect(params.source, params.target, params.sourceHandle || HANDLE_OUT)
  }, [onConnect])

  const handleConnectStart = useCallback((_, params) => {
    didConnectRef.current = false
    connectingFromRef.current = params && params.nodeId
      ? { nodeId: params.nodeId, handleType: params.handleType, handleId: params.handleId }
      : null
  }, [])

  const sourceHandleFree = useCallback((nodeId, sourceHandle) => {
    if (!nodeId) return false
    const sourceNode = getNodeById(chatbot, nodeId)
    const sourceData = sourceNode && sourceNode.type === NODE_TYPE_TEMPLATE
      ? overlayTemplateNodeDataFromMap(sourceNode.data, templatePreviewByKey)
      : (sourceNode && sourceNode.data)
    const validHandles = getOutgoingHandleIds(sourceData, sourceNode && sourceNode.type)
    const handle = sourceHandle || HANDLE_OUT
    if (validHandles.indexOf(handle) === -1) return false
    return !(chatbot.edges || []).some(e => (
      e.sourceNodeId === nodeId && edgeSourceHandle(e) === handle
    ))
  }, [chatbot, templatePreviewByKey])

  const closeAddMenu = useCallback(() => {
    setAddMenu(null)
  }, [])

  const handleAddMenuSelect = useCallback((type) => {
    const menu = addMenu
    setAddMenu(null)
    if (!menu || !type || !onAddConnectedNode) return
    const inst = instanceRef.current
    if (!inst || typeof inst.screenToFlowPosition !== 'function') return
    const p = inst.screenToFlowPosition({ x: menu.clientX, y: menu.clientY })
    if (!p || p.x == null || p.y == null) return
    onAddConnectedNode(menu.nodeId, menu.handleId, type, {
      posX: p.x,
      posY: p.y - ADD_OFFSET_Y,
    })
  }, [addMenu, onAddConnectedNode])

  const handleConnectEnd = useCallback((event) => {
    const from = connectingFromRef.current
    connectingFromRef.current = null
    if (didConnectRef.current || !from || !from.nodeId) return
    if (from.handleType !== 'source') return
    const point = eventClientPoint(event)
    const droppedId = (point && nodeIdFromPoint(point.x, point.y)) || nodeIdFromEvent(event)
    if (droppedId) {
      const sourceId = from.nodeId
      const targetId = droppedId
      const sourceHandle = from.handleId || HANDLE_OUT
      const connection = { source: sourceId, target: targetId, sourceHandle }
      if (!isValidConnection(connection)) return
      if (onConnect) onConnect(sourceId, targetId, sourceHandle)
      return
    }
    if (!onAddConnectedNode) return
    if (!point || !isPointInsideEl(wrapperRef.current, point)) return
    const sourceHandle = from.handleId || HANDLE_OUT
    if (!sourceHandleFree(from.nodeId, sourceHandle)) return
    setAddMenu({
      nodeId: from.nodeId,
      handleId: sourceHandle,
      clientX: point.x,
      clientY: point.y,
    })
  }, [isValidConnection, onAddConnectedNode, onConnect, sourceHandleFree])

  const handleNodeDragStart = useCallback(() => {
    didDragRef.current = false
  }, [])

  const handleNodeDrag = useCallback(() => {
    didDragRef.current = true
  }, [])

  const handleNodeDragStop = useCallback((event, node) => {
    if (!node || !onNodeDragStop) return
    onNodeDragStop(node.id, node.position.x, node.position.y)
  }, [onNodeDragStop])

  const handleNodeClick = useCallback((event, node) => {
    if (didDragRef.current) {
      didDragRef.current = false
      return
    }
    if (event && event.target && event.target.closest && event.target.closest('[data-chatbot-node-menu]')) {
      return
    }
    if (!node || !onNodeClick) return
    onNodeClick(node.id)
  }, [onNodeClick])

  useEffect(() => {
    const onKeyDown = (event) => {
      if (!pointerInsideRef.current || event.defaultPrevented) return
      if (isEditableTarget(event.target) || isEditableTarget(document.activeElement)) return
      if (document.querySelector('[role="dialog"]')) return
      const direction = canvasZoomDirection(event)
      if (!direction) return
      const inst = instanceRef.current
      if (!inst) return
      if (direction === 'in') inst.zoomIn()
      else inst.zoomOut()
      event.preventDefault()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  return (
    <div
      ref={wrapperRef}
      className={css(Styles.wrapper)}
      onPointerEnter={() => { pointerInsideRef.current = true }}
      onPointerLeave={() => { pointerInsideRef.current = false }}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        defaultEdgeOptions={defaultEdgeOptions}
        onNodesChange={onNodesChange}
        onConnect={handleConnect}
        onConnectStart={handleConnectStart}
        onConnectEnd={handleConnectEnd}
        onNodeDragStart={handleNodeDragStart}
        onNodeDrag={handleNodeDrag}
        onNodeDragStop={handleNodeDragStop}
        onNodeClick={handleNodeClick}
        onInit={handleInit}
        isValidConnection={isValidConnection}
        selectNodesOnDrag={false}
        connectionRadius={20}
        connectionLineStyle={edgeStyle}
        deleteKeyCode={null}
        multiSelectionKeyCode={null}
        fitView
        fitViewOptions={fitViewOptions}
        defaultViewport={{ x: 40, y: 40, zoom: 1 }}
        proOptions={{ hideAttribution: true }}
      >
        <Background />
        {((chatbot.nodes || []).length > 0) ? (
          <Controls showInteractive={false} className={css(Styles.controls)}>
            <ControlButton onClick={handleAutoLayout} title='auto layout' aria-label='auto layout'>
              <i className={'material-icons-outlined ' + css(Styles.controlIcon)}>account_tree</i>
            </ControlButton>
          </Controls>
        ) : null}
      </ReactFlow>
      <ChatbotAddNodeMenu
        open={!!addMenu}
        anchorPosition={addMenu ? { top: addMenu.clientY, left: addMenu.clientX } : undefined}
        onClose={closeAddMenu}
        onSelect={handleAddMenuSelect}
        palette={palette}
      />
    </div>
  )
})

const Styles = StyleSheet.create({
  wrapper: {
    width: '100%',
    height: '100%',
    ':nth-child(1n) .react-flow__nodesselection': {
      display: 'none',
    },
    ':nth-child(1n) .react-flow__selection': {
      display: 'none',
    },
  },
  controls: {
    boxShadow: 'none',
    border: '1px solid ' + AppTheme.borderColor,
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: AppTheme.secondaryBg,
    ':nth-child(1n) .react-flow__controls-button': {
      background: AppTheme.secondaryBg,
      borderBottom: '1px solid ' + AppTheme.borderColor,
      width: 18,
      height: 18,
    },
    ':nth-child(1n) .react-flow__controls-button:last-child': {
      borderBottom: 'none',
    },
    ':nth-child(1n) .react-flow__controls-button:hover': {
      background: AppTheme.primaryBg,
    },
  },
  controlIcon: {
    fontSize: 14,
  },
})
